/**
 * Typed calls for every endpoint the client uses. Paths come from
 * `Routes` and shapes from the protocol DTOs, so client and server never
 * disagree on spelling.
 */
import {
  reactionRouteOfToken,
  CallRoutes,
  Routes,
  type CallEligibilityResponse,
  type AuthSessionInfo,
  type AuthSessionResponse,
  type Channel,
  type ChangePasswordRequest,
  type ChangePasswordResponse,
  type CreateChannelRequest,
  type CreateMessageRequest,
  type CreatePrivateChannelRequest,
  type CreateRoleRequest,
  type EditChannelPermissionsRequest,
  type EditMessageRequest,
  type EnableTotpRequest,
  type EnableTotpResponse,
  type GetMessagesQuery,
  type GetPinsResponse,
  type GetReactionsQuery,
  type GetReactionsResponse,
  type Guild,
  type GuildMember,
  type LoginRequest,
  type LoginResponse,
  type Message,
  type MfaLoginRequest,
  type ModifyChannelRequest,
  type ModifyGuildRequest,
  type ModifyMemberRequest,
  type ModifyRoleRequest,
  type PrivateUser,
  type RegenerateRecoveryCodesResponse,
  type RegisterRequest,
  type Relationship,
  type Role,
  type SendFriendRequestRequest,
  type SetVoiceChannelStatusRequest,
  type Snowflake,
  type StepUpRequest,
  type StepUpResponse,
  type TotpSetupResponse,
  type UpdateCurrentUserRequest,
  type UpdateUserSettingsRequest,
  type UserProfile,
  type UserSettings,
  type VoiceTokenRequest,
  type VoiceTokenResponse,
  type HealthResponse,
  type DisableTotpRequest,
  type PowChallengeRequest,
  type PowChallengeResponse,
  type ForgotPasswordRequest,
  type ResetPasswordRequest,
  type SetEmailRequest,
  type VerifyEmailCodeRequest,
  type UndoEmailChangeRequest,
  type UndoEmailChangeResponse,
  type VerifyEmailLinkRequest,
  type VerifyEmailLinkResponse,
} from '@neosocial/protocol';
import { noteReach } from '../../store/reach';
import { apiUrl } from '../config';
import { ApiClient } from './client';
import { createTokenStore } from './tokens';
import { createUmApi } from './um';
import { createAutoModApi } from './automod';

export * from './client';

export function createApi(client: ApiClient) {
  return {
    client,
    /** User-management P1: moderation, invites, search, requests, recovery. */
    um: createUmApi(client),
    /** AutoMod rules (2026-10-29). */
    automod: createAutoModApi(client),
    health: () => client.get<HealthResponse>(Routes.health(), { auth: false }),
    auth: {
      powChallenge: (b: PowChallengeRequest = {}) =>
        client.post<PowChallengeResponse>(Routes.authPowChallenge(), b, { auth: false }),
      register: (b: RegisterRequest) =>
        client.post<AuthSessionResponse>(Routes.authRegister(), b, { auth: false }),
      login: (b: LoginRequest) =>
        client.post<LoginResponse>(Routes.authLogin(), b, { auth: false }),
      mfaTotp: (b: MfaLoginRequest) =>
        client.post<AuthSessionResponse>(Routes.authMfaTotp(), b, { auth: false }),
      mfaRecovery: (b: MfaLoginRequest) =>
        client.post<AuthSessionResponse>(Routes.authMfaRecovery(), b, { auth: false }),
      logout: () => client.post<void>(Routes.authLogout(), {}),
      stepUp: (b: StepUpRequest) => client.post<StepUpResponse>(Routes.authStepUp(), b),
      // email (2026-10-28): the link from an email works signed out too
      verifyEmailLink: (b: VerifyEmailLinkRequest) =>
        client.post<VerifyEmailLinkResponse>(Routes.authVerifyEmail(), b, { auth: false }),
      forgotPassword: (b: ForgotPasswordRequest) =>
        client.post<Record<string, never>>(Routes.authForgotPassword(), b, { auth: false }),
      resetPassword: (b: ResetPasswordRequest) =>
        client.post<void>(Routes.authResetPassword(), b, { auth: false }),
      // the old address's "This wasn't me" link after an email change (2026-10-29)
      undoEmailChange: (b: UndoEmailChangeRequest) =>
        client.post<UndoEmailChangeResponse>(Routes.authUndoEmailChange(), b, { auth: false }),
    },
    me: {
      get: () => client.get<PrivateUser>(Routes.me()),
      patch: (b: UpdateCurrentUserRequest) => client.patch<PrivateUser>(Routes.me(), b),
      password: (b: ChangePasswordRequest) =>
        client.post<ChangePasswordResponse>(Routes.mePassword(), b),
      setEmail: (b: SetEmailRequest) => client.put<PrivateUser>(Routes.meEmail(), b),
      verifyEmail: (b: VerifyEmailCodeRequest) =>
        client.post<PrivateUser>(Routes.meEmailVerify(), b),
      resendEmail: () => client.post<void>(Routes.meEmailResend(), {}),
      cancelPendingEmail: () => client.delete<void>(Routes.meEmailPending()),
      settings: () => client.get<UserSettings>(Routes.meSettings()),
      patchSettings: (b: UpdateUserSettingsRequest) =>
        client.patch<UserSettings>(Routes.meSettings(), b),
      sessions: () => client.get<AuthSessionInfo[]>(Routes.meSessions()),
      revokeSession: (id: string) => client.delete<void>(Routes.meSession(id)),
      logoutOthers: () => client.post<void>(Routes.meSessionsLogoutOthers(), {}),
      totpSetup: () => client.post<TotpSetupResponse>(Routes.meMfaTotpSetup(), {}),
      totpEnable: (b: EnableTotpRequest) => client.put<EnableTotpResponse>(Routes.meMfaTotp(), b),
      totpDisable: (b: DisableTotpRequest) => client.delete<void>(Routes.meMfaTotp(), { body: b }),
      regenerateCodes: () => client.post<RegenerateRecoveryCodesResponse>(Routes.meMfaCodes(), {}),
      relationships: () => client.get<Relationship[]>(Routes.meRelationships()),
      sendFriendRequest: (b: SendFriendRequestRequest) =>
        client.post<void>(Routes.meRelationships(), b),
      putRelationship: (id: Snowflake, type?: 1 | 2) =>
        client.put<void>(Routes.meRelationship(id), type ? { type } : {}),
      deleteRelationship: (id: Snowflake) => client.delete<void>(Routes.meRelationship(id)),
      openPrivateChannel: (b: CreatePrivateChannelRequest) =>
        client.post<Channel>(Routes.meChannels(), b),
    },
    users: {
      profile: (id: Snowflake) => client.get<UserProfile>(Routes.userProfile(id)),
    },
    guilds: {
      /** PATCH /guilds/{id}: name, icon (a data URI, or null to remove it), ... (Manage Server). */
      modify: (g: Snowflake, b: ModifyGuildRequest) => client.patch<Guild>(Routes.guild(g), b),
      createChannel: (g: Snowflake, b: CreateChannelRequest, reason?: string) =>
        client.post<Channel>(Routes.guildChannels(g), b, { reason }),
      createRole: (g: Snowflake, b: CreateRoleRequest) =>
        client.post<Role>(Routes.guildRoles(g), b),
      modifyRole: (g: Snowflake, r: Snowflake, b: ModifyRoleRequest) =>
        client.patch<Role>(Routes.guildRole(g, r), b),
      deleteRole: (g: Snowflake, r: Snowflake) => client.delete<void>(Routes.guildRole(g, r)),
      modifyMember: (g: Snowflake, u: Snowflake, b: ModifyMemberRequest) =>
        client.patch<GuildMember>(Routes.guildMember(g, u), b),
    },
    channels: {
      modify: (c: Snowflake, b: ModifyChannelRequest) =>
        client.patch<Channel>(Routes.channel(c), b),
      delete: (c: Snowflake) => client.delete<Channel>(Routes.channel(c)),
      addRecipient: (c: Snowflake, u: Snowflake) =>
        client.put<void>(Routes.channelRecipient(c, u), {}),
      removeRecipient: (c: Snowflake, u: Snowflake) =>
        client.delete<void>(Routes.channelRecipient(c, u)),
      putOverwrite: (c: Snowflake, id: Snowflake, b: EditChannelPermissionsRequest) =>
        client.put<void>(Routes.channelPermission(c, id), b),
      deleteOverwrite: (c: Snowflake, id: Snowflake) =>
        client.delete<void>(Routes.channelPermission(c, id)),
      typing: (c: Snowflake) => client.post<void>(Routes.channelTyping(c), {}),
      messages: (c: Snowflake, q: GetMessagesQuery) =>
        client.get<Message[]>(Routes.channelMessages(c), { query: { ...q } }),
      send: (
        c: Snowflake,
        b: CreateMessageRequest,
        files?: File[],
        o: { onUploadProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
      ) => {
        if (files && files.length) {
          const form = new FormData();
          form.set(
            'payload_json',
            JSON.stringify({
              ...b,
              attachments: files.map((f, i) => ({ id: i, filename: f.name })),
            }),
          );
          files.forEach((f, i) => form.set(`files[${i}]`, f, f.name));
          return client.post<Message>(Routes.channelMessages(c), undefined, { form, ...o });
        }
        return client.post<Message>(Routes.channelMessages(c), b);
      },
      edit: (c: Snowflake, m: Snowflake, b: EditMessageRequest) =>
        client.patch<Message>(Routes.channelMessage(c, m), b),
      deleteMessage: (c: Snowflake, m: Snowflake) =>
        client.delete<void>(Routes.channelMessage(c, m)),
      ack: (c: Snowflake, m: Snowflake) => client.post<void>(Routes.channelMessageAck(c, m), {}),
      pins: (c: Snowflake) => client.get<GetPinsResponse>(Routes.channelPins(c)),
      pin: (c: Snowflake, m: Snowflake) => client.put<void>(Routes.channelPin(c, m)),
      unpin: (c: Snowflake, m: Snowflake) => client.delete<void>(Routes.channelPin(c, m)),
      // a custom emoji's token (<:name:id>) goes as Discord's name:id
      react: (c: Snowflake, m: Snowflake, emoji: string) =>
        client.put<void>(Routes.messageOwnReaction(c, m, reactionRouteOfToken(emoji))),
      unreact: (c: Snowflake, m: Snowflake, emoji: string) =>
        client.delete<void>(Routes.messageOwnReaction(c, m, reactionRouteOfToken(emoji))),
      // polls (2026-10-29): your whole vote (empty takes it back), and ending one now
      pollVote: (c: Snowflake, m: Snowflake, answerIds: number[]) =>
        client.put<void>(Routes.pollVote(c, m), { answer_ids: answerIds }),
      pollExpire: (c: Snowflake, m: Snowflake) => client.post<Message>(Routes.pollExpire(c, m), {}),
      /** Who reacted (Discord's GET .../reactions/{emoji}): users by id, `after`, `limit` 1-100. */
      reactionUsers: (c: Snowflake, m: Snowflake, emoji: string, q: GetReactionsQuery = {}) =>
        client.get<GetReactionsResponse>(
          Routes.messageReaction(c, m, reactionRouteOfToken(emoji)),
          { query: { ...q } },
        ),
      /** A moderator takes someone's reaction off (Manage Messages). */
      removeUserReaction: (c: Snowflake, m: Snowflake, emoji: string, userId: Snowflake) =>
        client.delete<void>(Routes.messageUserReaction(c, m, reactionRouteOfToken(emoji), userId)),
    },
    voice: {
      token: (b: VoiceTokenRequest) => client.post<VoiceTokenResponse>(Routes.voiceToken(), b),
      /** Discord's voice channel status (2026-11-01): 204. */
      setStatus: (c: Snowflake, b: SetVoiceChannelStatusRequest) =>
        client.put<void>(`/channels/${c}/voice-status`, b),
    },
    // DM and group DM calls (2026-11-01)
    calls: {
      eligibility: (c: Snowflake) => client.get<CallEligibilityResponse>(CallRoutes.call(c)),
      ring: (c: Snowflake, recipients?: Snowflake[]) =>
        client.post<void>(CallRoutes.ring(c), recipients ? { recipients } : {}),
      stopRinging: (c: Snowflake, recipients?: Snowflake[]) =>
        client.post<void>(CallRoutes.stopRinging(c), recipients ? { recipients } : {}),
    },
  };
}

export type Api = ReturnType<typeof createApi>;

let sessionExpiredHandler: () => void = () => {};
/** The app sets this to sign out when the refresh token is refused. */
export function onSessionExpired(fn: () => void): void {
  sessionExpiredHandler = fn;
}

export const tokens = createTokenStore();
export const api: Api = createApi(
  new ApiClient({
    baseUrl: () => apiUrl(''),
    tokens,
    onSessionExpired: () => sessionExpiredHandler(),
    onReach: noteReach,
  }),
);
